/* screens/workspace/canvas/ChangeCanvas.jsx — канвас AI-правки СУЩЕСТВУЮЩЕЙ сущности.
   Полный диф «было → станет» pending-правки (PendingChange.preview.changes, считает
   бэкенд в rollback-транзакции: preview == будущий commit байт-в-байт): поля, тексты,
   состав ТТК (added/removed/changed), блюда меню — плюс итоги себест./FC/цена.
   Одобрение здесь же: Подтвердить / Отклонить / Откатить — общий хук usePendingChange
   с ConfirmActionCard (компакт-карточка прячется, пока канвас открыт, и подхватывает
   статус из pc.status после закрытия). Данные целиком в spec.pc (metadata сообщения) —
   загрузки нет. Открывается автоматически: canvasSpecFromMeta строит спеку из
   metadata.pending_changes. */

import React from 'react';
import { KindBadge } from '../../../components/ui/Badge.jsx';
import { Icon } from '../../../components/ui/Icon.jsx';
import { fmt } from '../../../lib/format.js';
import { KIND_HREF, KIND_LABEL, usePendingChange } from '../feed/usePendingChange.js';
import { CanvasSection, CanvasViewForm } from './CanvasViewForm.jsx';

  const num = (v) => (v == null || v === '' ? null : Number(v));
  const isNum = (v) => num(v) != null && !Number.isNaN(num(v));

  // Значение скаляра: числа — через num-форматирование, строки как есть.
  function ScalarVal({ v }) {
    if (v == null || v === '') return <span className="text-mute">—</span>;
    return isNum(v) ? <span className="num">{fmt.number ? fmt.number(num(v)) : num(v)}</span> : <span>{String(v)}</span>;
  }

  function ScalarRows({ items }) {
    return (
      <div className="cvch-scalars">
        {items.map((s) => (
          <div className="cvch-scalar" key={s.field}>
            <span className="cvch-scalar__lbl">{s.label}</span>
            <span className="cvch-scalar__from"><ScalarVal v={s.before} /></span>
            <Icon name="arrow-right" size={13} className="cvch-arr" />
            <span className="cvch-scalar__to"><ScalarVal v={s.after} /></span>
          </div>
        ))}
      </div>
    );
  }

  const grams = (v) => (v == null ? '—' : fmt.grams ? fmt.grams(v) : v + ' г');

  function RowAmount({ row }) {
    return (
      <span className="cvch-row__amount num">
        {grams(row.amount_brutto_g)} / {grams(row.amount_netto_g)}
      </span>
    );
  }

  function RowsDiff({ rows }) {
    const added = rows.added || [];
    const removed = rows.removed || [];
    const changed = rows.changed || [];
    if (!added.length && !removed.length && !changed.length) return null;
    return (
      <CanvasSection title="Состав" hint="брутто / нетто">
        <ul className="cvch-rows">
          {added.map((r, i) => (
            <li className="cvch-row cvch-row--add" key={'a' + i}>
              <span className="cvch-row__sign" aria-hidden="true">+</span>
              <span className="cvch-row__name truncate">{r.name}</span>
              <RowAmount row={r} />
            </li>
          ))}
          {removed.map((r, i) => (
            <li className="cvch-row cvch-row--del" key={'d' + i}>
              <span className="cvch-row__sign" aria-hidden="true">−</span>
              <span className="cvch-row__name truncate">{r.name}</span>
              <RowAmount row={r} />
            </li>
          ))}
          {changed.map((c, i) => (
            <li className="cvch-row cvch-row--mod" key={'c' + i}>
              <span className="cvch-row__sign" aria-hidden="true">±</span>
              <span className="cvch-row__name truncate">{c.name}</span>
              <span className="cvch-row__amount num">
                {grams(c.before.amount_brutto_g)} / {grams(c.before.amount_netto_g)}
                <Icon name="arrow-right" size={12} className="cvch-arr" />
                {grams(c.after.amount_brutto_g)} / {grams(c.after.amount_netto_g)}
              </span>
            </li>
          ))}
        </ul>
      </CanvasSection>
    );
  }

  function DishesDiff({ dishes }) {
    const added = dishes.added || [];
    const removed = dishes.removed || [];
    if (!added.length && !removed.length) return null;
    return (
      <CanvasSection title="Блюда меню">
        <ul className="cvch-rows">
          {added.map((d) => (
            <li className="cvch-row cvch-row--add" key={'a' + d.id}>
              <span className="cvch-row__sign" aria-hidden="true">+</span>
              <span className="cvch-row__name truncate">{d.name}</span>
            </li>
          ))}
          {removed.map((d) => (
            <li className="cvch-row cvch-row--del" key={'d' + d.id}>
              <span className="cvch-row__sign" aria-hidden="true">−</span>
              <span className="cvch-row__name truncate">{d.name}</span>
            </li>
          ))}
        </ul>
      </CanvasSection>
    );
  }

  function TextsDiff({ texts }) {
    if (!texts.length) return null;
    return (
      <CanvasSection title="Тексты">
        {texts.map((t) => (
          <div className="cvch-text" key={t.field}>
            <div className="cvch-text__lbl">{t.label}</div>
            {t.before ? <div className="cvch-text__from">{t.before}</div> : <div className="cvch-text__from text-mute">— пусто —</div>}
            <div className="cvch-text__to">{t.after || <span className="text-mute">— пусто —</span>}</div>
          </div>
        ))}
      </CanvasSection>
    );
  }

  function TotalRow({ label, before, after, fmtFn, betterWhenLower }) {
    if (before == null && after == null) return null;
    const changed = before !== after;
    const dirGood = betterWhenLower == null ? null : (after < before ? betterWhenLower : !betterWhenLower);
    return (
      <div className="ai-diff__row">
        <span className="ai-diff__key">{label}</span>
        <span className="ai-diff__from num">{fmtFn(before)}</span>
        <Icon name="arrow-right" size={13} className="ai-diff__arr" />
        <span className={'ai-diff__to num' + (changed && dirGood === true ? ' is-good' : changed && dirGood === false ? ' is-bad' : '')}>{fmtFn(after)}</span>
      </div>
    );
  }

  const BADGE_KIND = { ttk: 'dish', card: 'ingredient', menu: 'menu' };

  function ChangeCanvas({ spec, onClose, panel }) {
    const pc = spec.pc || {};
    const { status, busy, applied, restoreRef, commit, cancel, restore, restoreIdle } = usePendingChange(pc);
    const p = pc.preview || {};
    const ch = p.changes || {};
    const scalars = ch.scalars || [];
    const texts = ch.texts || [];
    const rows = ch.rows || {};
    const dishes = ch.dishes || {};

    // Итоги: оба варианта именования (живой кадр нормализован адаптером,
    // история — сырой preview бэка), как в ConfirmActionCard.
    const costBefore = num(p.cost_before);
    const costAfter = num(p.cost_after);
    const fcBefore = num(p.fc_pct_before != null ? p.fc_pct_before : p.fc_before);
    const fcAfter = num(p.fc_pct_after != null ? p.fc_pct_after : p.fc_after);
    const sellBefore = num(p.selling_price_before != null ? p.selling_price_before : p.selling_before);
    const sellAfter = num(p.selling_price_after != null ? p.selling_price_after : p.selling_after);
    const hasTotals = [costBefore, costAfter, fcBefore, fcAfter, sellBefore, sellAfter].some((v) => v != null);

    const hasFooter = status === 'idle' || status === 'error' || busy
      || (status === 'committed' && restoreRef) || status === 'cancelled';
    const footer = !hasFooter ? null : (
      <div className="cv-foot-actions cvch-foot">
        {(status === 'idle' || status === 'error') && (
          <>
            <button type="button" className="btn btn--primary btn--sm" onClick={commit}>
              <Icon name="check" size={14} /><span className="btn__label">{status === 'error' ? 'Повторить' : 'Утвердить изменения'}</span>
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={cancel}>Отклонить</button>
          </>
        )}
        {busy && (
          <button type="button" className="btn btn--primary btn--sm" disabled>
            <Icon name="spinner" size={14} className="spin" /><span className="btn__label">{status === 'restoring' ? 'Откат…' : 'Применяю…'}</span>
          </button>
        )}
        {status === 'committed' && restoreRef && (
          <button type="button" className="btn btn--ghost btn--sm" onClick={restore}><Icon name="refresh" size={14} /><span className="btn__label">Откатить</span></button>
        )}
        {status === 'cancelled' && (
          <button type="button" className="btn btn--ghost btn--sm" onClick={restoreIdle}>Вернуть к рассмотрению</button>
        )}
      </div>
    );

    return (
      <CanvasViewForm
        eyebrow={'Изменения на одобрение · ' + (KIND_LABEL[pc.kind] || 'сущность')}
        icon="sparkles"
        title={pc.title || 'Правка'}
        openHref={KIND_HREF(pc)} openLabel="Открыть в редакторе"
        onClose={onClose} panel={panel} busy={busy}
        footer={footer}
      >
        <div className="cv-meta-row">
          <KindBadge kind={BADGE_KIND[pc.kind] || 'dish'} size="sm" />
          {applied && <span className="tagpill tagpill--ok"><Icon name="check" size={12} />{status === 'restored' ? 'Откатано' : 'Применено'}</span>}
          {status === 'stale' && <span className="tagpill tagpill--warn"><Icon name="alert-triangle" size={12} />Сущность изменилась — правка устарела</span>}
          {status === 'cancelled' && <span className="tagpill tagpill--neutral"><Icon name="x-circle" size={12} />Отклонено</span>}
          {!applied && status !== 'stale' && status !== 'cancelled' && <span className="tagpill tagpill--info"><Icon name="clock" size={12} />ожидает одобрения</span>}
        </div>

        {p.summary && <p className="cvch-summary">{p.summary}</p>}
        {status === 'error' && <div className="canvas-error" role="alert"><Icon name="alert-circle" size={16} /><span className="grow">Не удалось применить. Повторите.</span></div>}

        {scalars.length > 0 && (
          <CanvasSection title="Поля"><ScalarRows items={scalars} /></CanvasSection>
        )}
        <RowsDiff rows={rows && typeof rows === 'object' ? rows : {}} />
        <DishesDiff dishes={dishes && typeof dishes === 'object' ? dishes : {}} />
        <TextsDiff texts={texts} />

        {hasTotals && (
          <CanvasSection title="Итоги">
            <div className="ai-diff cvch-totals">
              <TotalRow label="Себестоимость" before={costBefore} after={costAfter} fmtFn={(v) => fmt.money(v)} betterWhenLower={true} />
              <TotalRow label="Food cost" before={fcBefore} after={fcAfter} fmtFn={(v) => (v == null ? '—' : fmt.percent(v))} betterWhenLower={true} />
              <TotalRow label="Цена продажи" before={sellBefore} after={sellAfter} fmtFn={(v) => fmt.money(v)} betterWhenLower={false} />
            </div>
          </CanvasSection>
        )}
      </CanvasViewForm>
    );
  }

export { ChangeCanvas };
